import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionState,
  useLocalParticipant,
  useRoomContext,
  useTracks,
  TrackReference,
  VideoTrack,
  AudioTrack,
  useIsSpeaking,
  useRemoteParticipants
} from '@livekit/components-react';
import { ConnectionState, Track, RemoteAudioTrack } from 'livekit-client';
import { Rnd } from 'react-rnd';
import {
  MicIcon,
  CameraIcon,
  ScreenShareIcon,
  ChatIcon,
  ParticipantsIcon,
  NestorIcon,
  GridIcon,
  ExitIcon,
  SunIcon,
  MoonIcon,
  TranslateIcon,
  OffBar,
} from '../components/MeetIcons';
import dojoStageUrl from '../assets/dojo-stage-v2.png';
// Les styles LiveKit sont importés globalement dans main.tsx

// Configuration LiveKit Cloud - serveur hébergé par LiveKit
// Serveur media AUTO-HEBERGE sur M2 (27/09/2026). Signalisation par le tunnel
// Cloudflare ; le media va en direct en UDP 7882 / TCP 7881 vers la maison.
// Lisible depuis l'environnement pour qu'une bascule ne demande plus de rebuild.
const SERVER_URL = (import.meta as any).env?.VITE_LIVEKIT_URL || 'wss://livekit.infinity-cloud.ch';
const TOKEN_SERVER_URL = "/api/livekit/token"; // Utilise le proxy du serveur principal

type MeetExperience = 'default' | 'dojo';

const meetBackground = (experience: MeetExperience): string => {
  if (experience === 'dojo') {
    return `linear-gradient(180deg, rgba(4, 8, 15, 0.28), rgba(4, 8, 15, 0.58)), url(${dojoStageUrl}) center/cover no-repeat fixed`;
  }
  return (typeof window !== 'undefined' && (
    localStorage.getItem('meet_theme') === 'dark'
    || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768)
  ))
    ? 'url(/images/meet-background-dark.png?v=1) center/cover no-repeat fixed, linear-gradient(135deg, #0A0A0F 0%, #1a1a2e 100%)'
    : 'url(/images/meet-background.png?v=2) center/cover no-repeat fixed';
};

console.log('🌐 LiveKit Cloud:', SERVER_URL);

// Fonction pour générer un token via notre serveur proxy
const generateToken = async (roomName: string, participantName: string): Promise<string> => {
  try {
    console.log(`Demande de token pour ${participantName} dans la salle ${roomName}`);
    const response = await fetch(TOKEN_SERVER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roomName, participantName }),
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const data = await response.json();
    console.log("Token reçu:", data.token ? data.token.substring(0, 20) + '...' : 'null');
    return data.token;
  } catch (error) {
    console.error('Erreur lors de la génération du token:', error);
    throw error;
  }
};

// Composant pour afficher l'heure actuelle
const Clock: React.FC = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <div className="clock" style={{
      fontSize: '14px',
      fontWeight: 500,
      color: '#1f2937'
    }}>
      {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </div>
  );
};

// Import du type Participant
import type { Participant, LocalParticipant } from 'livekit-client';

// V0.9 LOT 2 — Hook WebAudio pour détecter le speaking du localParticipant.
// useIsSpeaking() de LiveKit dépend de la détection serveur 'activeSpeakersChanged'
// qui n'est pas fiable pour le local. On lit directement le niveau RMS du mic.
const useLocalSpeaking = (participant: Participant): boolean => {
  const [speaking, setSpeaking] = useState(false);
  useEffect(() => {
    if (!participant?.isLocal) return;
    const localP = participant as LocalParticipant;

    let audioCtx: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let raf = 0;
    let lastEdge = 0; // anti-clignote : maintient l'état 200ms après dernier dépassement

    const detach = () => {
      cancelAnimationFrame(raf);
      try { source?.disconnect(); } catch {}
      try { analyser?.disconnect(); } catch {}
      try { audioCtx?.close(); } catch {}
      audioCtx = null; analyser = null; source = null;
    };

    const attach = (mediaTrack: MediaStreamTrack) => {
      detach();
      audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      source = audioCtx.createMediaStreamSource(new MediaStream([mediaTrack]));
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const THRESH = 0.045;
      const HOLD_MS = 220;
      const tick = () => {
        analyser!.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        const now = performance.now();
        if (rms > THRESH) lastEdge = now;
        setSpeaking(now - lastEdge < HOLD_MS);
        raf = requestAnimationFrame(tick);
      };
      tick();
    };

    const tryAttach = () => {
      const pub: any = (localP as any).getTrackPublication?.(Track.Source.Microphone)
        || (localP as any).getTrack?.(Track.Source.Microphone);
      const mediaTrack: MediaStreamTrack | undefined = pub?.audioTrack?.mediaStreamTrack || pub?.track?.mediaStreamTrack;
      if (mediaTrack && !mediaTrack.muted) attach(mediaTrack);
      else { detach(); setSpeaking(false); }
    };

    tryAttach();
    const handler = () => tryAttach();
    (localP as any).on?.('localTrackPublished', handler);
    (localP as any).on?.('localTrackUnpublished', handler);
    (localP as any).on?.('trackMuted', handler);
    (localP as any).on?.('trackUnmuted', handler);

    return () => {
      (localP as any).off?.('localTrackPublished', handler);
      (localP as any).off?.('localTrackUnpublished', handler);
      (localP as any).off?.('trackMuted', handler);
      (localP as any).off?.('trackUnmuted', handler);
      detach();
    };
  }, [participant]);
  return speaking;
};

// V0.9 LOT 3 — tuile compacte translator avec drapeau + point pulsant émeraude.
const LANG_FLAGS: Record<string, { flag: string; label: string }> = {
  fr: { flag: '🇫🇷', label: 'Français' },
  en: { flag: '🇬🇧', label: 'English' },
  sk: { flag: '🇸🇰', label: 'Slovensky' },
  rw: { flag: '🇷🇼', label: 'Kinyarwanda' },
  ja: { flag: '🇯🇵', label: '日本語' },
};

const TranslatorTile: React.FC<{ participant: Participant }> = ({ participant }) => {
  const attrs: any = participant?.attributes || {};
  const targetLang: string = attrs.targetLang || '';
  const lang = LANG_FLAGS[targetLang] || { flag: '🌐', label: targetLang || 'lang ?' };
  return (
    <div
      title={`Traducteur ${lang.label}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 14px 8px',
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.18) 0%, rgba(16, 185, 129, 0.06) 100%)',
        border: '1px solid rgba(16, 185, 129, 0.4)',
        borderRadius: '14px',
        minWidth: '96px',
        position: 'relative',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.12)',
      }}
    >
      <span style={{ fontSize: '34px', lineHeight: 1, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))' }}>{lang.flag}</span>
      <span style={{
        fontSize: '11px',
        color: '#a7f3d0',
        fontWeight: 500,
        letterSpacing: '-0.01em',
        whiteSpace: 'nowrap',
      }}>
        Traducteur {lang.label}
      </span>
      <span style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        background: '#10b981',
        boxShadow: '0 0 8px rgba(16, 185, 129, 0.9)',
        animation: 'translatorPulseV09 1.6s ease-in-out infinite',
      }} />
    </div>
  );
};

// V0.9 LOT 2 — Ondes concentriques discrètes autour de l'avatar (3 anneaux décalés).
const SpeakingRing: React.FC<{ isSpeaking: boolean }> = ({ isSpeaking }) => {
  if (!isSpeaking) return null;
  return (
    <>
      {[0, 0.5, 1].map((delay, i) => (
        <div key={i} style={{
          position: 'absolute',
          inset: '-3px',
          borderRadius: '50%',
          border: '1.5px solid rgba(102, 126, 234, 0.55)',
          animation: `pulseRingV09 1.8s cubic-bezier(0.16, 1, 0.3, 1) infinite ${delay}s`,
          pointerEvents: 'none',
          willChange: 'transform, opacity',
        }} />
      ))}
    </>
  );
};

// Composant Avatar avec indicateur audio
// V0.9 LOT 2 : combine useIsSpeaking (remote) + useLocalSpeaking (WebAudio mic local)
const SpeakingAvatar: React.FC<{
  participant: Participant;
  size?: 'small' | 'medium' | 'large'
}> = ({ participant, size = 'medium' }) => {
  const remoteSpeaking = useIsSpeaking(participant);
  const localSpeaking = useLocalSpeaking(participant);
  const isSpeaking = participant?.isLocal ? localSpeaking : remoteSpeaking;

  const dimensions = {
    small: { avatar: 60, fontSize: 24 },
    medium: { avatar: 80, fontSize: 32 },
    large: { avatar: 150, fontSize: 60 }
  };

  const dim = dimensions[size];
  const displayName = (participant?.name || participant?.identity || '?').trim();
  const initial = displayName.charAt(0).toUpperCase() || '?';

  return (
    <div style={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <SpeakingRing isSpeaking={isSpeaking} />
      <div style={{
        width: `${dim.avatar}px`,
        height: `${dim.avatar}px`,
        borderRadius: '50%',
        background: isSpeaking
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768)))
              ? 'rgba(255, 255, 255, 0.18)'
              : 'rgba(20, 20, 30, 0.55)'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${dim.fontSize}px`,
        fontWeight: 600,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
        color: '#f3f4f6',
        letterSpacing: '-0.02em',
        border: isSpeaking
          ? '2px solid rgba(255, 255, 255, 0.55)'
          : ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768)))
              ? '1px solid rgba(255, 255, 255, 0.25)'
              : '1px solid rgba(0, 0, 0, 0.15)'),
        boxShadow: isSpeaking
          ? '0 0 18px rgba(102, 126, 234, 0.45)'
          : 'none',
        transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
      }}>
        {initial}
      </div>
    </div>
  );
};

// Composant pour le logo Infinity Cloud
const InfinityCloudLogo: React.FC = () => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  // V0.3 FIX BUG 3 : mobile portrait = pas de logo (pattern Zoom/Meet/FaceTime)
  if (isMobile) return null;
  return (
    <div className="infinity-cloud-logo" style={{
      width: '180px',
      height: '60px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <img
        src="/images/logo.png"
        alt="Infinity Cloud Logo"
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    </div>
  );
};

// Composant pour la barre d'état
const StatusBar: React.FC<{ userName: string; connectionStatus: string }> = ({ userName, connectionStatus }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  return (
    <div className="status-bar" style={{
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '0.5rem' : '2rem',
      padding: isMobile ? '4px 10px' : '0.5rem 1rem',
      background: 'rgba(255, 255, 255, 0.04)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderRadius: isMobile ? '999px' : '12px',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      borderTop: '1px solid rgba(255, 255, 255, 0.35)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
      transition: 'all 0.3s ease',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
      maxWidth: isMobile ? 'calc(100vw - 24px)' : 'none',
    }}>
      <Clock />
      <div className="connection-status" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.35rem',
        flexShrink: 0,
      }}>
        <span className={`status-dot ${connectionStatus !== 'connected' ? 'status-dot-disconnected' : ''}`} style={{
          width: isMobile ? '6px' : '8px',
          height: isMobile ? '6px' : '8px',
          borderRadius: '50%',
          backgroundColor: connectionStatus === 'connected' ? '#10b981' : '#ef4444',
          flexShrink: 0,
        }}></span>
        <span style={{ color: isMobile ? '#f3f4f6' : '#1f2937', fontSize: isMobile ? '12px' : '14px', whiteSpace: 'nowrap' }}>
          {connectionStatus === 'connected' ? 'En direct' : 'Déconnecté'}
        </span>
      </div>
      <div className="user-info" style={{
        color: isMobile ? '#f3f4f6' : '#1f2937',
        fontSize: isMobile ? '12px' : '14px',
        fontWeight: 500,
        flexShrink: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: isMobile ? '80px' : 'none',
      }}>
        <span>{userName}</span>
      </div>
    </div>
  );
};

// Composant pour surveiller l'état de la connexion
const ConnectionStateMonitor: React.FC<{ onConnectionChange: (state: ConnectionState) => void }> = ({ onConnectionChange }) => {
  const connectionState = useConnectionState();

  useEffect(() => {
    console.log('État de la connexion:', connectionState);
    onConnectionChange(connectionState);
  }, [connectionState, onConnectionChange]);

  return null;
};

// Composant Participants Panel
const ParticipantsPanel: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const room = useRoomContext();
  const tracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
  ]);

  const kickParticipant = async (participantIdentity: string) => {
    if (!room) return;

    if (!confirm(`Éjecter ${participantIdentity} de la salle ?`)) return;

    try {
      const res = await fetch('/api/livekit/kick', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomName: room.name,
          participantIdentity
        }),
      });
      const data = await res.json();
      if (data.success) {
        console.log(`✅ ${participantIdentity} éjecté`);
      } else {
        alert(`Erreur: ${data.message}`);
      }
    } catch (err) {
      console.error('Erreur kick:', err);
      alert('Erreur lors de l\'éjection');
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      right: (typeof window !== 'undefined' && window.innerWidth < 768) ? 0 : '16px',
      top: (typeof window !== 'undefined' && window.innerWidth < 768) ? 0 : '16px',
      bottom: (typeof window !== 'undefined' && window.innerWidth < 768) ? 0 : '120px',
      left: (typeof window !== 'undefined' && window.innerWidth < 768) ? 0 : 'auto',
      paddingTop: (typeof window !== 'undefined' && window.innerWidth < 768) ? 'env(safe-area-inset-top)' : 0,
      paddingBottom: (typeof window !== 'undefined' && window.innerWidth < 768) ? 'env(safe-area-inset-bottom)' : 0,
      width: (typeof window !== 'undefined' && window.innerWidth < 768) ? '100vw' : '350px',
      background: 'rgba(255, 255, 255, 0.04)',
      backdropFilter: 'blur(28px) saturate(180%)',
      WebkitBackdropFilter: 'blur(28px) saturate(180%)',
      borderRadius: '20px',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      borderTop: '1.5px solid rgba(255, 255, 255, 0.35)',
      boxShadow: '0 8px 32px rgba(31, 38, 135, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 4000,
      transition: 'all 0.3s ease'
    }}>
      <div style={{
        padding: '16px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h3 style={{ color: '#1f2937', margin: 0, fontSize: '18px', fontWeight: 600 }}>👥 Participants ({tracks.length})</h3>
        <button onClick={onClose} style={{
          background: 'transparent',
          border: 'none',
          color: '#6b7280',
          fontSize: '24px',
          cursor: 'pointer',
          padding: '4px'
        }}>×</button>
      </div>

      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {tracks.map((trackRef) => {
          const isLocalParticipant = trackRef.participant.identity === room?.localParticipant.identity;

          return (
            <div key={trackRef.participant.identity} style={{
              background: isLocalParticipant ? 'rgba(102, 126, 234, 0.1)' : 'rgba(0, 0, 0, 0.03)',
              padding: '12px',
              borderRadius: '12px',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              color: '#1f2937',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>
                  {trackRef.participant.identity} {isLocalParticipant && '(Vous)'}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {trackRef.participant.isCameraEnabled ? '📹' : '📷'} {trackRef.participant.isMicrophoneEnabled ? '🎤' : '🔇'}
                </div>
              </div>
              {!isLocalParticipant && (
                <button onClick={() => kickParticipant(trackRef.participant.identity)} style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  Éjecter
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Composant Chat
const ChatPanel: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<{ from: string; text: string; time: string }[]>([]);
  const room = useRoomContext();

  const sendMessage = () => {
    if (!message.trim() || !room) return;

    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify({
      from: room.localParticipant.identity,
      text: message,
      time: new Date().toLocaleTimeString()
    }));

    room.localParticipant.publishData(data, { reliable: true });

    setMessages(prev => [...prev, {
      from: 'Vous',
      text: message,
      time: new Date().toLocaleTimeString()
    }]);
    setMessage('');
  };

  useEffect(() => {
    if (!room) return;

    const handleData = (payload: Uint8Array, participant: any) => {
      const decoder = new TextDecoder();
      const data = JSON.parse(decoder.decode(payload));
      setMessages(prev => [...prev, {
        from: participant.identity,
        text: data.text,
        time: data.time
      }]);
    };

    room.on('dataReceived', handleData);
    return () => {
      room.off('dataReceived', handleData);
    };
  }, [room]);

  if (!isOpen) return null;

  // V0.3 FIX BUG 2 mobile drawer : sur mobile, panneau plein écran avec safe-area
  const isMobileChat = typeof window !== 'undefined' && window.innerWidth < 768;
  if (isMobileChat) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 20, 30, 0.96)',
        backdropFilter: 'blur(28px) saturate(180%)',
        WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        zIndex: 5000,
        display: 'flex',
        flexDirection: 'column',
        color: '#f3f4f6',
      }}>
        <div style={{
          padding: '16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h3 style={{ color: '#f3f4f6', margin: 0, fontSize: '18px', fontWeight: 600 }}>💬 Chat</h3>
          <button onClick={onClose} style={{
            background: 'transparent',
            border: 'none',
            color: '#f3f4f6',
            fontSize: '28px',
            cursor: 'pointer',
            padding: '4px 12px',
            minWidth: '44px',
            minHeight: '44px',
            lineHeight: 1,
          }}>×</button>
        </div>
        <div style={{ flex: 1, padding: '16px', overflowY: 'auto', color: '#f3f4f6' }}>
          <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: '40px' }}>
            Chat dans la conférence
          </div>
        </div>
      </div>
    );
  }
  return (
    <Rnd
      default={{
        x: typeof window !== 'undefined' ? window.innerWidth - 400 : 100,
        y: 100,
        width: 350,
        height: 500
      }}
      minWidth={300}
      minHeight={400}
      bounds="window"
      dragHandleClassName="chat-drag-handle"
      style={{
        zIndex: 5000
      }}
    >
      <div style={{
        width: '100%',
        height: '100%',
        background: 'rgba(255, 255, 255, 0.04)',
        backdropFilter: 'blur(28px) saturate(180%)',
        WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.25)',
        borderTop: '1.5px solid rgba(255, 255, 255, 0.35)',
        boxShadow: '0 8px 32px rgba(31, 38, 135, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <div className="chat-drag-handle" style={{
          padding: '16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'move'
        }}>
          <h3 style={{ color: '#1f2937', margin: 0, fontSize: '18px', fontWeight: 600 }}>💬 Chat</h3>
        <button onClick={onClose} style={{
          background: 'transparent',
          border: 'none',
          color: '#6b7280',
          fontSize: '24px',
          cursor: 'pointer',
          padding: '4px'
        }}>×</button>
      </div>

      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            background: msg.from === 'Vous' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(0, 0, 0, 0.03)',
            padding: '12px',
            borderRadius: '12px',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            color: '#1f2937'
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
              {msg.from} • {msg.time}
            </div>
            <div style={{ fontSize: '14px' }}>{msg.text}</div>
          </div>
        ))}
      </div>

      <div style={{ padding: '16px', borderTop: '1px solid rgba(0, 0, 0, 0.05)' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Tapez votre message..."
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              background: 'rgba(255, 255, 255, 0.5)',
              color: '#1f2937',
              outline: 'none',
              fontSize: '14px'
            }}
          />
          <button onClick={sendMessage} style={{
            padding: '12px 20px',
            borderRadius: '8px',
            border: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
          }}>
            Envoyer
          </button>
        </div>
      </div>
    </div>
    </Rnd>
  );
};

// Panneau de contrôle Nestor (Agent IA)
const NestorControlPanel: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const room = useRoomContext();
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }]);
  const [nestorMuted, setNestorMuted] = useState(false);
  const [targetParticipant, setTargetParticipant] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Charger l'état initial de Nestor
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch('/api/nestor/status');
        if (res.ok) {
          const data = await res.json();
          setNestorMuted(data.muted || false);
          setTargetParticipant(data.target_participant || null);
        }
      } catch (err) {
        console.error('Erreur chargement état Nestor:', err);
      }
    };
    fetchStatus();
    // Rafraîchir toutes les 2 secondes
    const interval = setInterval(fetchStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  const toggleNestorMute = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/nestor/mute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ muted: !nestorMuted }),
      });
      if (res.ok) {
        setNestorMuted(!nestorMuted);
      }
    } catch (err) {
      console.error('Erreur toggle mute Nestor:', err);
    }
    setLoading(false);
  };

  const setNestorTarget = async (participant: string | null) => {
    setLoading(true);
    try {
      const res = await fetch('/api/nestor/target', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant }),
      });
      if (res.ok) {
        setTargetParticipant(participant);
      }
    } catch (err) {
      console.error('Erreur set target Nestor:', err);
    }
    setLoading(false);
  };

  // Récupérer la liste des participants (exclure Nestor lui-même)
  const participants = tracks
    .filter(t => !t.participant.identity.toLowerCase().includes('nestor'))
    .map(t => t.participant);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      left: (typeof window !== 'undefined' && window.innerWidth < 768) ? 0 : '16px',
      top: (typeof window !== 'undefined' && window.innerWidth < 768) ? 0 : '100px',
      right: (typeof window !== 'undefined' && window.innerWidth < 768) ? 0 : 'auto',
      bottom: (typeof window !== 'undefined' && window.innerWidth < 768) ? 0 : 'auto',
      paddingTop: (typeof window !== 'undefined' && window.innerWidth < 768) ? 'env(safe-area-inset-top)' : 0,
      paddingBottom: (typeof window !== 'undefined' && window.innerWidth < 768) ? 'env(safe-area-inset-bottom)' : 0,
      width: (typeof window !== 'undefined' && window.innerWidth < 768) ? '100vw' : '320px',
      overflowY: (typeof window !== 'undefined' && window.innerWidth < 768) ? 'auto' : 'visible',
      background: 'rgba(255, 255, 255, 0.04)',
      backdropFilter: 'blur(28px) saturate(180%)',
      WebkitBackdropFilter: 'blur(28px) saturate(180%)',
      borderRadius: '20px',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      borderTop: '1.5px solid rgba(255, 255, 255, 0.35)',
      boxShadow: '0 8px 32px rgba(31, 38, 135, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
      zIndex: 4000,
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
      }}>
        <h3 style={{ color: '#1f2937', margin: 0, fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '24px' }}>🤖</span> Nestor IA
        </h3>
        <button onClick={onClose} style={{
          background: 'transparent',
          border: 'none',
          color: '#6b7280',
          fontSize: '24px',
          cursor: 'pointer',
          padding: '4px'
        }}>×</button>
      </div>

      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Bouton Mute/Unmute */}
        <div>
          <label style={{ display: 'block', color: '#374151', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
            État de Nestor
          </label>
          <button
            onClick={toggleNestorMute}
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px 20px',
              borderRadius: '12px',
              border: 'none',
              background: nestorMuted
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '15px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: nestorMuted
                ? '0 4px 12px rgba(239, 68, 68, 0.3)'
                : '0 4px 12px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.3s ease',
              opacity: loading ? 0.7 : 1
            }}
          >
            <span style={{ fontSize: '20px' }}>{nestorMuted ? '🔇' : '🎙️'}</span>
            {nestorMuted ? 'Nestor MUTÉ - Cliquer pour activer' : 'Nestor ACTIF - Cliquer pour muter'}
          </button>
          <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '6px', textAlign: 'center' }}>
            {nestorMuted ? "Nestor n'écoute plus et ne répond plus" : "Nestor écoute et répond aux participants"}
          </p>
        </div>

        {/* Info sur le routage */}
        <div style={{
          padding: '12px',
          background: 'rgba(251, 191, 36, 0.1)',
          borderRadius: '10px',
          border: '1px solid rgba(251, 191, 36, 0.3)'
        }}>
          <p style={{ color: '#92400e', fontSize: '13px', margin: 0 }}>
            <strong>💡 Astuce :</strong> Utilisez le bouton <strong>Mute</strong> ci-dessus pour que Nestor arrête d'écouter temporairement.
            Le routage par participant n'est pas encore disponible (limitation LiveKit).
          </p>
        </div>

        {/* Lien vers admin */}
        <a
          href="/meet/admin"
          target="_blank"
          style={{
            display: 'block',
            textAlign: 'center',
            color: '#667eea',
            fontSize: '13px',
            textDecoration: 'none',
            padding: '8px',
            borderRadius: '8px',
            background: 'rgba(102, 126, 234, 0.1)',
            marginTop: '8px'
          }}
        >
          ⚙️ Paramètres avancés (VAD, voix...)
        </a>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// V0.2 — Subscribe UNIQUEMENT translators dont targetLang === userLang.
// Mute systématique humains + translators autres langues.
// ═══════════════════════════════════════════════════════════════
const FilteredAudioRenderer: React.FC<{ userLang: string | null }> = ({ userLang }) => {
  // V0.4 FilteredAudioRenderer 3-layer mute :
  // L1 setSubscribed réseau / L2 setVolume RemoteAudioTrack / L3 HTMLAudioElement.muted DOM
  const audioTracks = useTracks([Track.Source.Microphone]);
  const applyAudioFilter = React.useCallback(() => {
    // V0.4 opt-in : si userLang null, mode normal direct (aucune traduction)
    const translationModeActive = userLang !== null && audioTracks.some((tr) => {
      const p = tr.participant;
      if (!p || p.isLocal) return false;
      const a = p.attributes || {};
      return a.role === 'translator' && a.targetLang === userLang;
    });

    audioTracks.forEach((trackRef) => {
      const pub = trackRef.publication;
      const participant = trackRef.participant;
      if (!pub || !participant) return;
      if (participant.isLocal) return;

      const attrs = participant.attributes || {};
      const isTranslator = attrs.role === 'translator';
      const translatorLang = attrs.targetLang;

      // DOJO : traduction exclusive. Dès qu'un traducteur de la langue choisie
      // est actif, la voix humaine originale est réellement muette ; entendre
      // les deux voix simultanément rend la conversation incompréhensible.
      let vol: number;
      let keepSubscribed: boolean;
      if (translationModeActive) {
        if (isTranslator && translatorLang === userLang) { vol = 1.0; keepSubscribed = true; }
        else if (isTranslator) { vol = 0.0; keepSubscribed = false; }
        else { vol = 0.0; keepSubscribed = true; }
      } else {
        vol = isTranslator ? 0.0 : 1.0;
        keepSubscribed = !isTranslator;
      }

      // L1 : LiveKit subscription (on ne désabonne QUE les translators inutiles)
      try { (pub as any).setSubscribed(keepSubscribed); } catch {}

      // L2 : volume sur RemoteAudioTrack
      try {
        const track = pub.track as RemoteAudioTrack | undefined;
        if (track && typeof (track as any).setVolume === 'function') {
          (track as any).setVolume(vol);
        }
      } catch {}

      // L3 : DOM <audio> — volume aligné, muted seulement si vol=0
      try {
        const sid = (participant as any).sid || (participant as any).identity || '';
        if (sid) {
          const selector = 'audio[data-lk-participant-id="' + sid + '"], audio[data-lk-participant-sid="' + sid + '"]';
          const els = document.querySelectorAll(selector);
          els.forEach((el) => {
            const audioEl = el as HTMLAudioElement;
            audioEl.volume = vol;
            // ON RELIT CE QU'ON VIENT DE POSER. iOS ignore le volume par element : la valeur ne
            // prend pas, sans la moindre erreur. Plutot que de renifler la plateforme, on constate.
            const aPris = Math.abs(audioEl.volume - vol) < 0.02;
            audioEl.muted = vol === 0 || (!aPris && vol < 0.5);
          });
        }
      } catch {}
    });
  }, [audioTracks, userLang]);

  useEffect(() => {
    applyAudioFilter();
    const t1 = setTimeout(applyAudioFilter, 250);
    const t2 = setTimeout(applyAudioFilter, 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [applyAudioFilter]);

  return <RoomAudioRenderer />;
};

// V0.9 LOT 4 — contrôle Translators directement dans /meet
// (évite l'aller-retour sur /meet/admin). Auth admin HelloPanda transparente.
const PAIR_TO_LANGS: Record<string, string[]> = {
  'fr-sk': ['fr', 'sk'],
  'fr-en': ['fr', 'en'],
  'en-sk': ['en', 'sk'],
  'fr-rw': ['fr', 'rw'],
  'fr-ja': ['fr', 'ja'],
  'en-ja': ['en', 'ja'],
};
const PAIR_LABELS: Record<string, string> = {
  'fr-sk': '🇫🇷 ↔ 🇸🇰  Français ↔ Slovensky',
  'fr-en': '🇫🇷 ↔ 🇬🇧  Français ↔ English',
  'en-sk': '🇬🇧 ↔ 🇸🇰  English ↔ Slovensky',
  'fr-rw': '🇫🇷 ↔ 🇷🇼  Français ↔ Kinyarwanda',
  'fr-ja': '🇫🇷 ↔ 🇯🇵  Français ↔ 日本語',
  'en-ja': '🇬🇧 ↔ 🇯🇵  English ↔ 日本語',
};

const TranslatorsControl: React.FC<{
  roomName: string;
  isMobile: boolean;
  ControlButton: React.FC<any>;
  userLang: string | null;
  setUserLang: (lang: string | null) => void;
}> = ({ roomName, isMobile, ControlButton, userLang, setUserLang }) => {
  const remoteParticipants = useRemoteParticipants();
  const { localParticipant } = useLocalParticipant();
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [pwd, setPwd] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [authErr, setAuthErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // V1.2 — Auto-discovery des langues à activer (plus de notion de "paire" UX).
  // = union { userLang } ∪ { humanLang de chaque autre humain présent ayant set sa langue }.
  // Le backend /start dispatch automatiquement les translators nécessaires.
  const remoteHumans = remoteParticipants.filter(p => {
    const a: any = p.attributes || {};
    return a.role !== 'translator' && !a.targetLang;
  });
  const remoteLangs = Array.from(new Set(
    remoteHumans
      .map(p => ((p.attributes as any) || {}).humanLang)
      .filter(l => l && ['fr','en','sk','rw','ja'].includes(l))
  )) as string[];
  const detectedLanguages = Array.from(new Set([
    ...(userLang ? [userLang] : []),
    ...remoteLangs,
  ])).filter(Boolean) as string[];
  const FLAG_FOR: Record<string, string> = { fr: '🇫🇷', en: '🇬🇧', sk: '🇸🇰', rw: '🇷🇼', ja: '🇯🇵' };
  const LABEL_FOR: Record<string, string> = { fr: 'Français', en: 'English', sk: 'Slovensky', rw: 'Kinyarwanda', ja: '日本語' };
  const [msg, setMsg] = useState<string | null>(null);
  const [activeCount, setActiveCount] = useState(0);
  // V2 28 mai 2026 — sélecteur paire FR↔EN / FR↔SK
  const [selectedPair, setSelectedPair] = useState<[string, string]>(['fr', 'sk']);
  const AVAILABLE_PAIRS: Array<{ key: string; pair: [string, string]; flag1: string; flag2: string; label: string }> = [
    { key: 'fr-en', pair: ['fr', 'en'], flag1: '🇫🇷', flag2: '🇬🇧', label: 'FR ↔ EN' },
    { key: 'fr-sk', pair: ['fr', 'sk'], flag1: '🇫🇷', flag2: '🇸🇰', label: 'FR ↔ SK' },
    { key: 'fr-rw', pair: ['fr', 'rw'], flag1: '🇫🇷', flag2: '🇷🇼', label: 'FR ↔ RW' },
    { key: 'fr-ja', pair: ['fr', 'ja'], flag1: '🇫🇷', flag2: '🇯🇵', label: 'FR ↔ JA' },
    { key: 'en-ja', pair: ['en', 'ja'], flag1: '🇬🇧', flag2: '🇯🇵', label: 'EN ↔ JA' },
  ];

  // V13 AGI — dérive aussi activeCount des participants LiveKit live.
  // Plus fiable que le poll API qui peut rater (cookie pas reposé après reload).
  // Source : si un participant a role=translator → actif.
  const liveTranslatorCount = remoteParticipants.filter(p => {
    const a: any = p.attributes || {};
    return a.role === 'translator' || !!a.targetLang;
  }).length;
  const effectiveActiveCount = Math.max(activeCount, liveTranslatorCount);

  // Vérif auth + poll status quand modal ouverte
  useEffect(() => {
    if (!open) return;
    fetch('/api/admin/me', { credentials: 'include' })
      .then(r => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, [open]);

  // Poll statut translators (toutes les 4s) quand authed
  useEffect(() => {
    if (!roomName) return;
    let stopped = false;
    const tick = async () => {
      try {
        const r = await fetch(
          `/api/livekit/translators/status?roomName=${encodeURIComponent(roomName)}`,
          { credentials: 'include' }
        );
        if (!r.ok) return;
        const d = await r.json();
        if (!stopped && d.success) setActiveCount((d.translators || []).length);
      } catch {}
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { stopped = true; clearInterval(id); };
  }, [roomName, open, busy]);

  const login = async () => {
    setAuthBusy(true); setAuthErr(null);
    try {
      const r = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: pwd }),
      });
      if (r.ok) { setAuthed(true); setPwd(''); }
      else setAuthErr('Mot de passe incorrect');
    } catch (e: any) { setAuthErr(e?.message || 'Erreur'); }
    finally { setAuthBusy(false); }
  };

  // V2.2 28 mai 2026 — appelle DIRECTEMENT le script bash via endpoint dédié.
  // Le backend exécute /tmp/start_translators.sh roomName lang1 lang2 en spawn,
  // zéro divergence d'interprétation possible avec ce qui marche en SSH manuel.
  const startTr = async () => {
    setBusy(true); setMsg(null);
    try {
      const [lang1, lang2] = selectedPair;
      const r = await fetch('/api/livekit/translators/start-script', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ roomName, lang1, lang2 }),
      });
      const d = await r.json();
      if (d.success) {
        setMsg(`✓ traducteurs lancés (${lang1} ↔ ${lang2})`);
      } else {
        // Le script bash sort des messages d'erreur lisibles dans stdout (ex "il faut 2 humains présents")
        const errMsg = (d.stdout && d.stdout.includes('ERREUR')) ? d.stdout.split('ERREUR:')[1]?.trim() : null;
        setMsg(`Erreur : ${errMsg || d.error || d.stderr || d.stdout || 'inconnue'}`);
      }
    } catch (e: any) { setMsg(`Erreur : ${e?.message || 'inconnue'}`); }
    finally { setBusy(false); }
  };

  const stopTr = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch('/api/livekit/translators/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ roomName }),
      });
      const d = await r.json();
      if (d.success) {
        // V1.2 — optimistic UI : on déclare arrêté tout de suite,
        // puis poll rapide (700ms × 8 = 5.6s) pour reconverger sur la vérité backend.
        // Bug racine fixé côté serveur: deleteDispatch empêche LiveKit de re-spawn.
        setActiveCount(0);
        setMsg(`✓ ${d.count} translator(s) arrêté(s)`);
        let tries = 0;
        const fastPoll = setInterval(async () => {
          tries += 1;
          try {
            const sr = await fetch(
              `/api/livekit/translators/status?roomName=${encodeURIComponent(roomName)}`,
              { credentials: 'include' }
            );
            if (sr.ok) {
              const sd = await sr.json();
              if (sd.success) setActiveCount((sd.translators || []).length);
            }
          } catch {}
          if (tries >= 8) clearInterval(fastPoll);
        }, 700);
      } else {
        setMsg(`Erreur : ${d.error || 'inconnue'}`);
      }
    } catch (e: any) { setMsg(`Erreur : ${e?.message || 'inconnue'}`); }
    finally { setBusy(false); }
  };

  const active = effectiveActiveCount > 0;

  return (
    <>
      <ControlButton
        Icon={TranslateIcon}
        label={active ? 'Arrêter trad.' : 'Traducteurs'}
        isActive={active}
        onClick={() => setOpen(true)}
      />
      {open && typeof document !== 'undefined' && createPortal(
        // V1.2 — Portal vers document.body pour échapper aux ancêtres
        // avec backdrop-filter (control bar) qui créent un containing block
        // et brisent position:fixed en iOS Safari. Sans Portal, la modale
        // colle au bas du viewport au lieu d'être centrée.
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
            paddingLeft: '16px', paddingRight: '16px',
            boxSizing: 'border-box',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '380px', width: '100%',
              maxHeight: '100%',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              boxSizing: 'border-box',
              background: 'linear-gradient(180deg, #1a1a2e 0%, #0A0A0F 100%)',
              border: '1px solid rgba(212, 164, 55, 0.15)',
              borderRadius: '20px',
              padding: '24px',
              color: '#f3f4f6',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              boxShadow: '0 24px 64px rgba(0,0,0,0.8)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
              <TranslateIcon size={22} />
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600, letterSpacing: '-0.01em' }}>
                Traducteurs temps réel
              </h3>
            </div>

            {authed === null && (
              <div style={{ color: '#9ca3af', fontSize: '13px' }}>Vérification…</div>
            )}

            {authed === false && (
              <div>
                <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: 0 }}>
                  Authentification admin requise (1 fois / 24h).
                </p>
                <input
                  type="password"
                  value={pwd}
                  onChange={e => setPwd(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && pwd && login()}
                  placeholder="Mot de passe"
                  autoFocus
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 14px', borderRadius: '12px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    color: '#f3f4f6', fontSize: '14px', outline: 'none',
                  }}
                />
                {authErr && (
                  <div style={{ color: '#fca5a5', fontSize: '12px', marginTop: '8px' }}>{authErr}</div>
                )}
                <button
                  onClick={login}
                  disabled={authBusy || !pwd}
                  style={{
                    marginTop: '14px', width: '100%',
                    padding: '11px', borderRadius: '12px',
                    background: '#d4a437', color: '#0A0A0F',
                    border: 'none', fontSize: '14px', fontWeight: 600,
                    cursor: authBusy || !pwd ? 'not-allowed' : 'pointer',
                    opacity: authBusy || !pwd ? 0.5 : 1,
                    transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                  }}
                >
                  {authBusy ? '…' : 'Se connecter'}
                </button>
              </div>
            )}

            {authed === true && (
              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>
                  Room : <span style={{ color: '#f3f4f6', fontFamily: 'monospace' }}>{roomName}</span>
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '14px' }}>
                  Translators actifs : <span style={{ color: active ? '#10b981' : '#f3f4f6', fontWeight: 600 }}>{effectiveActiveCount}</span>
                </div>

                {/* V2.3 28 mai 2026 — sélecteur + bouton Activer TOUJOURS visibles.
                    Au clic Activer, le script start_translators.sh fait stop+start
                    automatiquement. Donc tu peux re-cliquer même si déjà actif pour
                    relancer proprement sans avoir à passer par Arrêter d'abord. */}
                {true && (
                  <>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                      Choisir la paire :
                    </div>
                    <div style={{
                      display: 'flex', gap: '10px', marginBottom: '16px',
                    }}>
                      {AVAILABLE_PAIRS.map((p) => {
                        const sel = p.pair[0] === selectedPair[0] && p.pair[1] === selectedPair[1];
                        return (
                          <button
                            key={p.key}
                            onClick={() => setSelectedPair(p.pair)}
                            style={{
                              flex: 1,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                              padding: '14px 8px',
                              borderRadius: '12px',
                              background: sel ? 'rgba(212, 164, 55, 0.18)' : 'rgba(255,255,255,0.04)',
                              border: sel ? '1.5px solid rgba(212, 164, 55, 0.6)' : '1px solid rgba(255,255,255,0.1)',
                              color: '#f3f4f6',
                              cursor: 'pointer',
                              transition: 'all 0.18s cubic-bezier(0.16,1,0.3,1)',
                            }}
                          >
                            <span style={{ fontSize: '20px' }}>{p.flag1}</span>
                            <span style={{ fontSize: '14px', color: sel ? '#d4a437' : '#6b7280', fontWeight: 300 }}>↔</span>
                            <span style={{ fontSize: '20px' }}>{p.flag2}</span>
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={startTr}
                      disabled={busy}
                      style={{
                        width: '100%', padding: '12px', borderRadius: '12px',
                        background: busy ? 'rgba(212, 164, 55, 0.4)' : '#d4a437',
                        color: '#0A0A0F',
                        border: 'none', fontSize: '15px', fontWeight: 600,
                        cursor: busy ? 'not-allowed' : 'pointer',
                        opacity: busy ? 0.6 : 1,
                        transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                        boxShadow: busy ? 'none' : '0 4px 16px rgba(212, 164, 55, 0.25)',
                      }}
                    >
                      {busy ? '…' : (active ? 'Relancer la traduction' : 'Activer la traduction')}
                    </button>
                  </>
                )}

                <button
                  onClick={stopTr}
                  disabled={busy}
                  style={{
                    width: '100%', padding: '11px', borderRadius: '12px',
                    marginTop: '10px',
                    background: 'rgba(239, 68, 68, 0.15)', color: '#fca5a5',
                    border: '1px solid rgba(239, 68, 68, 0.45)',
                    fontSize: '14px', fontWeight: 600,
                    cursor: busy ? 'not-allowed' : 'pointer',
                    opacity: busy ? 0.5 : 1,
                    transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                  }}
                >
                  {busy ? '…' : 'Arrêter les traducteurs'}
                </button>

                {/* V2 28 mai 2026 — Spawn Nestor depuis le globe (parité avec bouton PTT barre Meet) */}
                <button
                  onClick={async () => {
                    try {
                      await fetch('/api/nestor/spawn', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ roomName }),
                      });
                      setMsg('Nestor activé. Maintiens le bouton Nestor de la barre pour lui parler.');
                    } catch (e) { setMsg('Erreur spawn Nestor'); }
                  }}
                  style={{
                    width: '100%', padding: '11px', borderRadius: '12px',
                    marginTop: '10px',
                    background: 'rgba(102, 126, 234, 0.15)', color: '#a5b4fc',
                    border: '1px solid rgba(102, 126, 234, 0.45)',
                    fontSize: '14px', fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                  }}
                >
                  Activer Nestor
                </button>

                {/* 28 mai 2026 — Eteindre Nestor (kick room + kill worker) */}
                <button
                  onClick={async () => {
                    try {
                      const r = await fetch("/api/nestor/stop", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ roomName }),
                      });
                      const j = await r.json().catch(() => ({}));
                      if (j && j.success) setMsg("Nestor éteint.");
                      else setMsg("Erreur arrêt Nestor");
                    } catch (e) { setMsg("Erreur arrêt Nestor"); }
                  }}
                  style={{
                    width: "100%", padding: "11px", borderRadius: "12px",
                    marginTop: "10px",
                    background: "rgba(239, 68, 68, 0.15)", color: "#fca5a5",
                    border: "1px solid rgba(239, 68, 68, 0.45)",
                    fontSize: "14px", fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s cubic-bezier(0.16,1,0.3,1)",
                  }}
                >
                  Éteindre Nestor
                </button>

                {msg && (
                  <div style={{
                    marginTop: '12px', padding: '10px 12px',
                    borderRadius: '10px', fontSize: '13px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#d1d5db',
                  }}>{msg}</div>
                )}
              </div>
            )}

            <button
              onClick={() => setOpen(false)}
              style={{
                marginTop: '14px', width: '100%',
                padding: '9px', borderRadius: '10px',
                background: 'transparent', color: '#9ca3af',
                border: '1px solid rgba(255,255,255,0.1)',
                fontSize: '13px', cursor: 'pointer',
              }}
            >
              Fermer
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Composant personnalisé avec tous les contrôles visibles - Design moderne

// V2 28 mai 2026 — bouton push-to-talk Nestor (talkie-walkie).
// Maintenir = écoute active (pulse rouge), relâcher = mute + Nestor répond.
// Spawn auto le worker à la 1ère pression si pas encore actif dans la room.
const NestorPTTButton: React.FC<{
  roomName: string;
  requesterIdentity: string;
  isMobile: boolean;
}> = ({ roomName, requesterIdentity, isMobile }) => {
  const [pressed, setPressed] = useState(false);
  const [spawned, setSpawned] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [continuous, setContinuous] = useState(false);
  const iconSize = isMobile ? 22 : 20;

  useEffect(() => {
    fetch('/api/nestor/status')
      .then((response) => response.json())
      .then((data) => setContinuous(data.listening_mode === 'continuous'))
      .catch(() => {});
  }, []);

  const spawnIfNeeded = async () => {
    if (spawned) return;
    try {
      await fetch('/api/nestor/spawn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomName }),
      });
      setSpawned(true);
    } catch (e) { console.error('[Nestor spawn]', e); }
  };

  const startPTT = async (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (continuous) return;
    setPressed(true);
    await spawnIfNeeded();
    try {
      await fetch('/api/nestor/ptt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: true, requester: requesterIdentity }),
      });
    } catch (err) { console.error('[Nestor PTT start]', err); }
  };

  const stopPTT = async (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (continuous) return;
    setPressed(false);
    setThinking(true);
    try {
      await fetch('/api/nestor/ptt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: false }),
      });
    } catch (err) { console.error('[Nestor PTT stop]', err); }
    setTimeout(() => setThinking(false), 4000);
  };

  const toggleListeningMode = async () => {
    await spawnIfNeeded();
    const next = continuous ? 'ptt' : 'continuous';
    try {
      const response = await fetch('/api/nestor/listen-mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: next }),
      });
      const data = await response.json();
      if (data.success) setContinuous(data.listening_mode === 'continuous');
    } catch (err) { console.error('[Nestor listen mode]', err); }
  };

  const bg = continuous
    ? 'rgba(16, 185, 129, 0.28)'
    : pressed
    ? 'rgba(239, 68, 68, 0.85)'
    : thinking
      ? 'rgba(212, 164, 55, 0.25)'
      : 'rgba(0, 0, 0, 0.4)';
  const border = continuous
    ? '1px solid rgba(52, 211, 153, 0.75)'
    : pressed
    ? '1px solid rgba(255, 107, 107, 0.9)'
    : thinking
      ? '1px solid rgba(212, 164, 55, 0.6)'
      : '1px solid rgba(212, 164, 55, 0.15)';
  const color = continuous ? '#a7f3d0' : pressed ? '#FFFFFF' : thinking ? '#d4a437' : '#F0F0F0';
  const label = continuous ? 'Écoute continue' : pressed ? 'Écoute…' : thinking ? 'Réponse…' : 'Nestor';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <button
      onMouseDown={startPTT}
      onMouseUp={stopPTT}
      onMouseLeave={(e) => { if (pressed) stopPTT(e); }}
      onTouchStart={startPTT}
      onTouchEnd={stopPTT}
      title={continuous ? 'Nestor écoute continuellement' : 'Maintenir pour parler à Nestor'}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? '0' : '8px',
        padding: isMobile ? '8px' : '10px 16px',
        minWidth: isMobile ? '40px' : 'auto',
        width: isMobile ? '40px' : 'auto',
        height: isMobile ? '40px' : 'auto',
        borderRadius: '12px',
        border,
        background: bg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        color,
        cursor: continuous ? 'default' : 'pointer',
        transition: 'all 150ms cubic-bezier(0.16, 1, 0.3, 1)',
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
        letterSpacing: '-0.011em',
        boxShadow: pressed
          ? '0 0 0 4px rgba(239, 68, 68, 0.25), 0 0 20px rgba(239, 68, 68, 0.5)'
          : 'none',
        animation: pressed ? 'nestor-pulse 1.2s ease-in-out infinite' : 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      <span style={{ position: 'relative', display: 'inline-flex', lineHeight: 0 }}>
        <NestorIcon size={iconSize} active={continuous || pressed || thinking} />
      </span>
      {!isMobile && <span style={{ fontSize: '13px', fontWeight: 500 }}>{label}</span>}
      <style>{`
        @keyframes nestor-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.25), 0 0 20px rgba(239, 68, 68, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.15), 0 0 28px rgba(239, 68, 68, 0.7); }
        }
      `}</style>
    </button>
    <button
      type="button"
      onClick={toggleListeningMode}
      title={continuous ? 'Revenir au mode talkie-walkie' : 'Activer l’écoute continue'}
      aria-pressed={continuous}
      style={{
        width: isMobile ? '40px' : 'auto',
        height: isMobile ? '40px' : 'auto',
        padding: isMobile ? '8px' : '10px 12px',
        borderRadius: '12px',
        border: continuous ? '1px solid rgba(52, 211, 153, 0.75)' : '1px solid rgba(212, 164, 55, 0.15)',
        background: continuous ? 'rgba(16, 185, 129, 0.24)' : 'rgba(0, 0, 0, 0.4)',
        color: continuous ? '#a7f3d0' : '#F0F0F0',
        cursor: 'pointer',
        fontSize: isMobile ? '18px' : '12px',
        fontWeight: 600,
      }}
    >
      {isMobile ? '∞' : (continuous ? 'Continu' : 'PTT')}
    </button>
    </div>
  );
};

const CustomControlBar: React.FC<{
  onToggleChat: () => void;
  onToggleParticipants: () => void;
  onToggleFreeFloat: () => void;
  onToggleNestor: () => void;
  isFreeFloatMode: boolean;
  isNestorPanelOpen: boolean;
  userLang: string | null;
  setUserLang: (lang: string | null) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  token: string | null;
}> = ({ onToggleChat, onToggleParticipants, onToggleFreeFloat, onToggleNestor, isFreeFloatMode, isNestorPanelOpen, userLang, setUserLang, theme, toggleTheme, token }) => {
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();

  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [isCameraEnabled, setIsCameraEnabled] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  // Mic ON par défaut (condition de la traduction), caméra OFF par défaut.
  // 28 mai 2026 — directive Commandant : éviter le bug récurrent "translator dispatché sans mic actif".
  useEffect(() => {
    if (localParticipant) {
      localParticipant.setMicrophoneEnabled(true);
      localParticipant.setCameraEnabled(false);

      setIsMicEnabled(localParticipant.isMicrophoneEnabled);
      setIsCameraEnabled(localParticipant.isCameraEnabled);
    }
  }, [localParticipant]);

  // humanLang : ECRIT PAR LE SERVEUR (14 aout 2026). L'ancien chemin — le participant publie
  // lui-meme son attribut — ne fonctionnait pas : l'appel partait et EXPIRAIT (« Request to update
  // local metadata timed out »), droit canUpdateOwnMetadata accorde et verifie dans le jeton
  // decode, SDK a jour. Resultat : humanLang etait undefined pour TOUT LE MONDE, aussi bien depuis
  // les drapeaux en salle que depuis l'ecran d'entree, et personne ne le voyait parce que le log
  // annoncait « published » AVANT tout acquittement.
  useEffect(() => {
    if (!localParticipant || !userLang || !token) return;
    let annule = false;
    (async () => {
      try {
        const r = await fetch('/api/livekit/my-lang', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, lang: userLang }),
        });
        const data = await r.json().catch(() => ({}));
        if (annule) return;
        // Journal NON OPTIMISTE : on ne parle de succes qu'apres la reponse, et l'echec se dit
        // aussi fort que le succes.
        if (r.ok && data.success) console.log('[lang] humanLang ecrit par le serveur :', userLang);
        else console.warn('[lang] ECHEC ecriture humanLang :', r.status, data.error || '');
      } catch (e) {
        if (!annule) console.warn('[lang] ECHEC reseau humanLang :', e);
      }
    })();
    return () => { annule = true; };
  }, [localParticipant, userLang, token]);

  // Écouter les changements d'état des tracks
  useEffect(() => {
    if (!localParticipant) return;

    const handleTrackMuted = () => {
      setIsMicEnabled(localParticipant.isMicrophoneEnabled);
      setIsCameraEnabled(localParticipant.isCameraEnabled);
    };

    localParticipant.on('trackMuted', handleTrackMuted);
    localParticipant.on('trackUnmuted', handleTrackMuted);
    localParticipant.on('localTrackPublished', handleTrackMuted);
    localParticipant.on('localTrackUnpublished', handleTrackMuted);

    return () => {
      localParticipant.off('trackMuted', handleTrackMuted);
      localParticipant.off('trackUnmuted', handleTrackMuted);
      localParticipant.off('localTrackPublished', handleTrackMuted);
      localParticipant.off('localTrackUnpublished', handleTrackMuted);
    };
  }, [localParticipant]);

  const toggleMic = async () => {
    if (localParticipant) {
      await localParticipant.setMicrophoneEnabled(!isMicEnabled);
      setIsMicEnabled(localParticipant.isMicrophoneEnabled);
    }
  };

  const toggleCamera = async () => {
    if (localParticipant) {
      await localParticipant.setCameraEnabled(!isCameraEnabled);
      setIsCameraEnabled(localParticipant.isCameraEnabled);
    }
  };

  const toggleScreenShare = async () => {
    if (!localParticipant) return;

    if (isScreenSharing) {
      await localParticipant.setScreenShareEnabled(false);
      setIsScreenSharing(false);
    } else {
      await localParticipant.setScreenShareEnabled(true);
      setIsScreenSharing(true);
    }
  };

  const leaveRoom = () => {
    if (room) {
      room.disconnect();
      window.location.reload();
    }
  };

  // Détecter si on est sur mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const ControlButton: React.FC<{
    Icon: React.FC<{ size?: number; active?: boolean; danger?: boolean }>;
    label: string;
    isActive: boolean;
    onClick: () => void;
    variant?: 'danger';
    isOff?: boolean;
  }> = ({ Icon, label, isActive, onClick, variant, isOff }) => {
    const iconSize = isMobile ? 22 : 20;
    return (
      <button
        onClick={onClick}
        title={label}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? '0' : '8px',
          padding: isMobile ? '8px' : '10px 16px',
          minWidth: isMobile ? '40px' : 'auto',
          width: isMobile ? '40px' : 'auto',
          height: isMobile ? '40px' : 'auto',
          borderRadius: '12px',
          // V1.1 — palette ferme : accent jaune (active), rouge plat (danger), neutre sinon
          border: variant === 'danger'
            ? '1px solid rgba(255, 107, 107, 0.5)'
            : isActive
              ? '1px solid rgba(212, 164, 55, 0.5)'
              : '1px solid rgba(212, 164, 55, 0.15)',
          background: variant === 'danger'
            ? '#FF6B6B'
            : isActive
              ? 'rgba(212, 164, 55, 0.18)'
              : 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          color: variant === 'danger'
            ? '#FFFFFF'
            : isActive
              ? '#d4a437'
              : '#F0F0F0',
          cursor: 'pointer',
          transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
          letterSpacing: '-0.011em',
          boxShadow: 'none',
        }}
      >
        <span style={{ position: 'relative', display: 'inline-flex', lineHeight: 0 }}>
          <Icon size={iconSize} active={isActive} danger={variant === 'danger'} />
          {isOff && <OffBar size={iconSize} />}
        </span>
        {!isMobile && <span style={{ fontSize: '13px', fontWeight: 500 }}>{label}</span>}
      </button>
    );
  };

  return (
    <div style={{
      // V0.3 mobile Option A : barre pleine largeur dark glassmorphism + safe area
      background: isMobile ? 'rgba(20, 20, 30, 0.85)' : 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderTop: isMobile ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
      border: isMobile ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
      position: 'fixed',
      bottom: 0,
      left: isMobile ? 0 : '50%',
      right: isMobile ? 0 : 'auto',
      transform: isMobile ? 'none' : 'translateX(-50%)',
      width: isMobile ? '100vw' : 'auto',
      padding: isMobile
        ? '8px 10px calc(8px + env(safe-area-inset-bottom)) 10px'
        : '12px 20px',
      borderRadius: isMobile ? '16px 16px 0 0' : '16px',
      marginBottom: isMobile ? 0 : '1rem',
      display: 'flex',
      flexDirection: 'row',
      // V0.3 FIX BUG 1+5 : single-flex centré pattern FaceTime, jamais d'espacement extrême sur mobile
      justifyContent: 'center',
      alignItems: 'center',
      gap: isMobile ? '6px' : '10px',
      flexWrap: 'nowrap',
      maxWidth: '100vw',
      zIndex: 1000,
      boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.4)',
    }}>
        {/* ═══ V0.3 ZONE GAUCHE — Sun/Moon uniquement (V1.2 : drapeaux déplacés dans LangSidebar) ═══ */}
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {/* V0.7 Toggle thème clair/sombre */}
        <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Passer en thème clair' : 'Passer en thème sombre'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '6px 8px' : '8px 12px',
            borderRadius: isMobile ? '16px' : '8px',
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            color: theme === 'dark' ? '#f3f4f6' : '#1f2937',
            cursor: 'pointer',
            lineHeight: 0,
            marginLeft: '4px',
            flexShrink: 0,
          }}
        >
          {theme === 'dark' ? <SunIcon size={isMobile ? 18 : 20} /> : <MoonIcon size={isMobile ? 18 : 20} />}
        </button>
        </div>
        {/* ═══ V0.3 ZONE CENTRE — boutons principaux ═══ */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-end' : 'center', gap: isMobile ? '6px' : '8px', flex: '1 1 auto', minWidth: 0, flexWrap: 'nowrap' }}>
      <ControlButton
        Icon={MicIcon}
        label={isMicEnabled ? "Micro" : "Muet"}
        isActive={isMicEnabled}
        isOff={!isMicEnabled}
        onClick={toggleMic}
      />

      <ControlButton
        Icon={CameraIcon}
        label={isCameraEnabled ? "Caméra" : "Caméra OFF"}
        isActive={isCameraEnabled}
        isOff={!isCameraEnabled}
        onClick={toggleCamera}
      />

      <ControlButton
        Icon={ScreenShareIcon}
        label={isScreenSharing ? "Arrêter" : "Partager"}
        isActive={isScreenSharing}
        onClick={toggleScreenShare}
      />

      <ControlButton
        Icon={ChatIcon}
        label="Chat"
        isActive={false}
        onClick={onToggleChat}
      />

      {!isMobile && (
        <>
          <ControlButton
            Icon={ParticipantsIcon}
            label="Participants"
            isActive={false}
            onClick={onToggleParticipants}
          />

          <NestorPTTButton
            roomName={room.name}
            requesterIdentity={room.localParticipant?.identity || 'unknown'}
            isMobile={isMobile}
          />

          <ControlButton
            Icon={GridIcon}
            label={isFreeFloatMode ? "Mode Grille" : "Fenêtres"}
            isActive={isFreeFloatMode}
            onClick={onToggleFreeFloat}
          />
        </>
      )}

      {/* V1.1+ — Translators control TOUJOURS visible (room.name suffit).
          Le globe est l'entry point pour lancer les translators, il doit être
          accessible avant même que l'utilisateur ait choisi son humanLang. */}
      {room?.name && (
        <TranslatorsControl
          roomName={room.name}
          isMobile={isMobile}
          ControlButton={ControlButton}
          userLang={userLang}
          setUserLang={setUserLang}
        />
      )}

        </div>
        {/* ═══ V0.3 ZONE DROITE — Quitter ═══ */}
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <ControlButton
        Icon={ExitIcon}
        label="Quitter"
        isActive={false}
        onClick={leaveRoom}
        variant="danger"
      />
        </div>
    </div>
  );
};

// Type pour les positions des fenêtres
interface WindowPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Grille de vidéos des participants avec fenêtres flottantes redimensionnables
const ParticipantGrid: React.FC<{
  freeFloatMode: boolean;
  setFreeFloatMode: (value: boolean) => void;
}> = ({ freeFloatMode, setFreeFloatMode }) => {
  const room = useRoomContext();
  const allTracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ]);
  const [hoveredTrack, setHoveredTrack] = React.useState<string | null>(null);
  const [fullscreenTrack, setFullscreenTrack] = React.useState<string | null>(null);
  const [windowPositions, setWindowPositions] = React.useState<Record<string, WindowPosition>>({});

  // Identifier le participant local
  const localParticipantId = room?.localParticipant?.identity;

  // V1.2 — Filtre translators au niveau global tracks AVANT toute décision layout.
  // Un translator n'a pas de track caméra réel (audio-only). Il ne doit JAMAIS apparaître
  // comme tile dans la grille principale ni en floating window. Visible uniquement via
  // TranslatorStrip (drapeaux bas) + LangSidebar (droite). Filtre défensif :
  //   - attributes.role === 'translator' (canonique)
  //   - OU attributes.targetLang présent (translator a toujours targetLang)
  const isTranslatorTrack = (t: typeof allTracks[0]) => {
    const a: any = t.participant.attributes || {};
    return a.role === 'translator' || !!a.targetLang;
  };
  const tracks = allTracks.filter(t => !isTranslatorTrack(t));

  // Séparer les partages d'écran des caméras
  // IMPORTANT: Séparer les partages locaux des distants pour éviter l'effet miroir
  const allScreenShareTracks = tracks.filter(t => t.source === Track.Source.ScreenShare);
  const remoteScreenShareTracks = allScreenShareTracks.filter(t => t.participant.identity !== localParticipantId);
  const localScreenShareTracks = allScreenShareTracks.filter(t => t.participant.identity === localParticipantId);

  // Seuls les partages DISTANTS sont affichés en grand
  const screenShareTracks = remoteScreenShareTracks;
  const cameraTracks = tracks.filter(t => t.source === Track.Source.Camera);
  // V1.2 — Translators dérivés directement de useRemoteParticipants
  // (plus fiable que useTracks+placeholder car les agents audio-only
  // n'ont parfois pas de placeholder track généré côté SDK).
  const remoteParticipants = useRemoteParticipants();
  const translatorParticipants = remoteParticipants.filter(p => {
    const a: any = p.attributes || {};
    return a.role === 'translator' || !!a.targetLang;
  });

  // Initialiser les positions des fenêtres pour les nouveaux tracks
  useEffect(() => {
    const newPositions = { ...windowPositions };
    let hasChanges = false;

    tracks.forEach((track, index) => {
      const trackKey = `${track.participant.identity}-${track.source}`;
      if (!newPositions[trackKey]) {
        // Positions en cascade
        newPositions[trackKey] = {
          x: 50 + (index * 30),
          y: 50 + (index * 30),
          width: track.source === Track.Source.ScreenShare ? 600 : 400,
          height: track.source === Track.Source.ScreenShare ? 400 : 300
        };
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setWindowPositions(newPositions);
    }
  }, [tracks.length]);

  // Debug: Afficher les tracks détectées
  useEffect(() => {
    console.log('📹 TRACKS DETECTED:', tracks.length);
    console.log('🖥️  Screen shares:', screenShareTracks.length);
    console.log('📹 Cameras:', cameraTracks.length);
    tracks.forEach((track, index) => {
      console.log(`Track ${index}:`, {
        participant: track.participant.identity,
        source: track.source,
        hasPublication: !!track.publication,
        hasTrack: !!track.publication?.track,
        kind: track.publication?.kind
      });
    });
  }, [tracks, screenShareTracks.length, cameraTracks.length]);

  // Layout: Si partages d'écran, les afficher en grand avec caméras en petit
  const hasScreenShares = screenShareTracks.length > 0;

  // Rendu d'une fenêtre flottante (mode Free Float)
  const renderFloatingWindow = (trackRef: typeof tracks[0], isScreenShare: boolean) => {
    const trackKey = `${trackRef.participant.identity}-${trackRef.source}`;
    const position = windowPositions[trackKey] || { x: 50, y: 50, width: 600, height: 450 };
    const isHovered = hoveredTrack === trackKey;

    const isVideoTrack = trackRef.publication?.track &&
                        (trackRef.source === Track.Source.Camera ||
                         trackRef.source === Track.Source.ScreenShare);

    return (
      <Rnd
        key={trackKey}
        size={{ width: position.width, height: position.height }}
        position={{ x: position.x, y: position.y }}
        onDragStop={(e, d) => {
          setWindowPositions(prev => ({
            ...prev,
            [trackKey]: { ...position, x: d.x, y: d.y }
          }));
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          setWindowPositions(prev => ({
            ...prev,
            [trackKey]: {
              x: position.x,
              y: position.y,
              width: parseInt(ref.style.width),
              height: parseInt(ref.style.height)
            }
          }));
        }}
        minWidth={400}
        minHeight={300}
        bounds="parent"
        dragHandleClassName="drag-handle"
        enableResizing={{
          top: false,
          right: true,
          bottom: true,
          left: false,
          topRight: false,
          bottomRight: true,
          bottomLeft: false,
          topLeft: false,
        }}
        resizeHandleStyles={{
          bottomRight: {
            width: '20px',
            height: '20px',
            right: '4px',
            bottom: '4px',
            cursor: 'nwse-resize',
            zIndex: 20
          },
          right: {
            width: '8px',
            height: '100%',
            right: '0px',
            cursor: 'ew-resize'
          },
          bottom: {
            height: '8px',
            width: '100%',
            bottom: '0px',
            cursor: 'ns-resize'
          }
        }}
        style={{
          zIndex: isHovered ? 5000 : 4000
        }}
      >
        <div
          onMouseEnter={() => setHoveredTrack(trackKey)}
          onMouseLeave={() => setHoveredTrack(null)}
          onDoubleClick={() => isScreenShare && setFullscreenTrack(trackKey)}
          style={{
            width: '100%',
            height: '100%',
            // V1.6 28 mai 2026 — frosted glass theme-aware aligné sur le mode grille.
            background: ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768))))
              ? (isHovered
                ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 40%, rgba(255, 255, 255, 0.10) 100%), rgba(255, 255, 255, 0.06)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 40%, rgba(255, 255, 255, 0.08) 100%), rgba(255, 255, 255, 0.05)')
              : (isHovered
                ? 'linear-gradient(135deg, rgba(20, 20, 30, 0.22) 0%, rgba(20, 20, 30, 0.08) 40%, rgba(20, 20, 30, 0.15) 100%), rgba(20, 20, 30, 0.10)'
                : 'linear-gradient(135deg, rgba(20, 20, 30, 0.18) 0%, rgba(20, 20, 30, 0.06) 40%, rgba(20, 20, 30, 0.12) 100%), rgba(20, 20, 30, 0.08)'),
            backdropFilter: ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768))))
              ? 'blur(24px) saturate(200%) brightness(1.05)'
              : 'blur(24px) saturate(180%) brightness(0.95)',
            WebkitBackdropFilter: ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768))))
              ? 'blur(24px) saturate(200%) brightness(1.05)'
              : 'blur(24px) saturate(180%) brightness(0.95)',
            border: isScreenShare
              ? '1px solid rgba(212, 164, 55, 0.5)'
              : (((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768))))
                ? '1px solid rgba(212, 164, 55, 0.15)'
                : '1px solid rgba(0, 0, 0, 0.08)'),
            borderRadius: '20px',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768))))
              ? (isHovered
                ? 'inset 0 1px 0 rgba(255, 255, 255, 0.18), inset 0 -1px 0 rgba(0, 0, 0, 0.2), 0 16px 40px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.05)'
                : 'inset 0 1px 0 rgba(255, 255, 255, 0.12), inset 0 -1px 0 rgba(0, 0, 0, 0.15), 0 10px 28px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.03)')
              : (isHovered
                ? 'inset 0 1px 0 rgba(0, 0, 0, 0.08), inset 0 -1px 0 rgba(255, 255, 255, 0.3), 0 12px 32px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.06)'
                : 'inset 0 1px 0 rgba(0, 0, 0, 0.06), inset 0 -1px 0 rgba(255, 255, 255, 0.25), 0 8px 22px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04)'),
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'move'
          }}
        >
          {/* Handle de déplacement */}
          <div
            className="drag-handle"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '36px',
              background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
              cursor: 'move',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.3s'
            }}
          >
            <span style={{ color: 'white', fontSize: '12px', fontWeight: 600 }}>
              ⣿ Déplacer
            </span>
          </div>

          {/* Vidéo */}
          {isVideoTrack ? (
            <VideoTrack trackRef={trackRef as TrackReference} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SpeakingAvatar participant={trackRef.participant} size="medium" />
            </div>
          )}

          {/* V1.1 — name tag glass dark cohérent */}
          <div style={{
            position: 'absolute',
            bottom: '12px',
            left: '12px',
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(212, 164, 55, 0.15)',
            color: '#F0F0F0',
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 500,
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
            letterSpacing: '-0.011em',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            {trackRef.participant.identity}
          </div>

          {/* Indicateur de resize (visuel seulement) */}
          <div style={{
            position: 'absolute',
            bottom: '4px',
            right: '4px',
            width: '20px',
            height: '20px',
            background: 'rgba(102, 126, 234, 0.6)',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isHovered ? 1 : 0.3,
            transition: 'opacity 0.3s',
            pointerEvents: 'none',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}>
            <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>↘</span>
          </div>
        </div>
      </Rnd>
    );
  };

  const renderTrack = (trackRef: typeof tracks[0], isScreenShare: boolean, isSmall: boolean = false, fullbleed: boolean = false) => {
    const isVideoTrack = trackRef.publication?.track &&
                        (trackRef.source === Track.Source.Camera ||
                         trackRef.source === Track.Source.ScreenShare);

    const trackKey = `${trackRef.participant.identity}-${trackRef.source}`;
    const isHovered = hoveredTrack === trackKey;

    return (
      <div
        key={trackKey}
        onMouseEnter={() => setHoveredTrack(trackKey)}
        onMouseLeave={() => setHoveredTrack(null)}
        onClick={() => isScreenShare && setFullscreenTrack(trackKey)}
        style={{
          cursor: isScreenShare ? 'pointer' : 'default',
          // V1.6 28 mai 2026 — frosted glass theme-aware (verre brossé).
          // Dark mode : voile blanc translucide qui révèle le marbre noir.
          // Light mode : voile sombre translucide qui crée du contraste sur le marbre blanc
          // (sinon les lettres avatar se confondent avec le voile blanc).
          background: ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768)))
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 40%, rgba(255, 255, 255, 0.08) 100%), rgba(255, 255, 255, 0.05)'
            : 'linear-gradient(135deg, rgba(20, 20, 30, 0.18) 0%, rgba(20, 20, 30, 0.06) 40%, rgba(20, 20, 30, 0.12) 100%), rgba(20, 20, 30, 0.08)'),
          backdropFilter: ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768)))
            ? 'blur(24px) saturate(200%) brightness(1.05)'
            : 'blur(24px) saturate(180%) brightness(0.95)'),
          WebkitBackdropFilter: ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768)))
            ? 'blur(24px) saturate(200%) brightness(1.05)'
            : 'blur(24px) saturate(180%) brightness(0.95)'),
          border: isScreenShare
            ? '1px solid rgba(212, 164, 55, 0.4)'
            : '1px solid rgba(212, 164, 55, 0.15)',
          borderRadius: isSmall ? '12px' : '20px',
          overflow: 'hidden',
          position: 'relative',
          // V1.2 — fullbleed pour 1 participant solo : remplit le conteneur,
          // pas de ratio forcé qui imprime un cadre 16:9 sur viewport portrait
          aspectRatio: fullbleed ? undefined : '16/9',
          height: fullbleed ? '100%' : undefined,
          minHeight: fullbleed ? '60vh' : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768))))
            ? (isHovered
              ? 'inset 0 1px 0 rgba(255, 255, 255, 0.18), inset 0 -1px 0 rgba(0, 0, 0, 0.2), 0 16px 40px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.05)'
              : 'inset 0 1px 0 rgba(255, 255, 255, 0.12), inset 0 -1px 0 rgba(0, 0, 0, 0.15), 0 10px 28px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.03)')
            : (isHovered
              ? 'inset 0 1px 0 rgba(0, 0, 0, 0.08), inset 0 -1px 0 rgba(255, 255, 255, 0.3), 0 12px 32px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.06)'
              : 'inset 0 1px 0 rgba(0, 0, 0, 0.06), inset 0 -1px 0 rgba(255, 255, 255, 0.25), 0 8px 22px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04)'),
          transform: isHovered ? 'translateY(-2px) scale(1.005)' : 'translateY(0) scale(1)',
          transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
          width: '100%',
        }}>
        {isVideoTrack ? (
          <VideoTrack trackRef={trackRef as TrackReference} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SpeakingAvatar participant={trackRef.participant} size={isSmall ? 'small' : 'medium'} />
          </div>
        )}
        {/* V1.1 — name tag glass dark cohérent */}
        <div style={{
          position: 'absolute',
          bottom: '12px',
          left: '12px',
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(212, 164, 55, 0.15)',
          color: '#F0F0F0',
          padding: isSmall ? '5px 10px' : '6px 12px',
          borderRadius: '8px',
          fontSize: isSmall ? '11px' : '12px',
          fontWeight: 500,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
          letterSpacing: '-0.011em',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          {trackRef.participant.identity}
        </div>
        {/* Hint pour agrandir */}
        {isScreenShare && isHovered && !isSmall && (
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(102, 126, 234, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            animation: 'fadeIn 0.3s ease',
            pointerEvents: 'none'
          }}>
            🔍 Cliquer pour agrandir
          </div>
        )}
      </div>
    );
  };

  // Mode fenêtres flottantes
  if (freeFloatMode) {
    return (
      <div style={{
        flex: 1,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Fenêtres flottantes */}
        {tracks.map(track => renderFloatingWindow(
          track,
          track.source === Track.Source.ScreenShare
        ))}
      </div>
    );
  }

  // Mode plein écran
  if (fullscreenTrack) {
    const fullscreenTrackRef = tracks.find(t => `${t.participant.identity}-${t.source}` === fullscreenTrack);

    if (fullscreenTrackRef) {
      const isVideoTrack = fullscreenTrackRef.publication?.track &&
                          (fullscreenTrackRef.source === Track.Source.Camera ||
                           fullscreenTrackRef.source === Track.Source.ScreenShare);

      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.95)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          padding: 0
        }}>
          {/* Bouton fermer */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenTrack(null);
            }}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              color: 'white',
              padding: '12px 24px',
              fontSize: '18px',
              fontWeight: 600,
              cursor: 'pointer',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            ✕ Quitter plein écran
          </button>

          {/* Vidéo en plein écran */}
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px 20px 20px'
          }}>
            {isVideoTrack ? (
              <VideoTrack
                trackRef={fullscreenTrackRef as TrackReference}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: '12px'
                }}
              />
            ) : (
              <SpeakingAvatar participant={fullscreenTrackRef.participant} size="large" />
            )}
          </div>

          {/* Info participant */}
          <div style={{
            position: 'absolute',
            bottom: '30px',
            left: '30px',
            background: 'rgba(102, 126, 234, 0.9)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '12px',
            fontSize: '20px',
            fontWeight: 600,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            🖥️ {fullscreenTrackRef.participant.identity}
          </div>
        </div>
      );
    }
  }

  if (hasScreenShares) {
    // Layout avec partages d'écran: grands partages en haut, petites caméras en bas
    return (
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '16px',
        gap: '16px',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Partages d'écran (grands) */}
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: screenShareTracks.length === 1 ? '1fr' : 'repeat(2, 1fr)',
          gap: '16px',
          minHeight: 0
        }}>
          {screenShareTracks.map(track => renderTrack(track, true, false))}
        </div>

        {/* Caméras + partage local en petit (pour éviter effet miroir) */}
        {(cameraTracks.length > 0 || localScreenShareTracks.length > 0) && (
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            maxHeight: '180px'
          }}>
            {/* Partage d'écran LOCAL en miniature avec badge "Votre partage" */}
            {localScreenShareTracks.map(track => (
              <div key={`${track.participant.identity}-${track.source}`} style={{
                width: '220px',
                height: '140px',
                position: 'relative'
              }}>
                {renderTrack(track, true, true)}
                {/* Badge "Votre partage" */}
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  left: '8px',
                  background: 'rgba(102, 126, 234, 0.95)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: 600,
                  zIndex: 10
                }}>
                  Votre partage
                </div>
              </div>
            ))}
            {/* Caméras des autres participants */}
            {cameraTracks.map(track => (
              <div key={`${track.participant.identity}-${track.source}`} style={{
                width: '220px',
                height: '140px'
              }}>
                {renderTrack(track, false, true)}
              </div>
            ))}
          </div>
        )}
        {/* V0.9 LOT 3 — bandeau translators compact */}
        {translatorParticipants.length > 0 && (
          <TranslatorStrip participants={translatorParticipants} />
        )}
      </div>
    );
  }

  // Layout normal sans partage d'écran
  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      gap: '14px',
      overflow: 'auto',
      position: 'relative'
    }}>
      {/* V1.2 — Layout adaptatif :
          - 1 humain solo : tile fullbleed responsive (pas de cadre 600x400 fixe)
          - 2 humains : 2 colonnes
          - 3+ : auto-fit */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: cameraTracks.length === 1 ? '1fr' : cameraTracks.length === 2 ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '16px',
        width: '100%',
        height: 'auto',
        maxHeight: '100%',
        flex: '0 0 auto',
        placeItems: 'center',
      }}>
        {cameraTracks.map(track => {
          // V1.3 28 mai 2026 — fullbleed solo UNIQUEMENT en portrait mobile.
          // Sur web/desktop/iPad paysage : tuile 16:9 centrée comme en multi-participants.
          const isPortraitMobile = typeof window !== 'undefined'
            && window.innerWidth < window.innerHeight
            && window.innerWidth < 768;
          const useFullbleed = cameraTracks.length === 1 && isPortraitMobile;
          return renderTrack(track, false, false, useFullbleed);
        })}
      </div>
      {/* V0.9 LOT 3 — bandeau translators compact */}
      {translatorParticipants.length > 0 && (
        <TranslatorStrip participants={translatorParticipants} />
      )}
    </div>
  );
};

// V0.9 LOT 3 — Bandeau bas compact regroupant les translators actifs.
const TranslatorStrip: React.FC<{ participants: Participant[] }> = ({ participants }) => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 14px',
      background: 'rgba(20, 20, 30, 0.55)',
      borderRadius: '16px',
      border: '1px solid rgba(16, 185, 129, 0.25)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      maxWidth: '100%',
    }}>
      <span style={{
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: '#a7f3d0',
        fontWeight: 600,
        opacity: 0.8,
      }}>
        Translators actifs
      </span>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {participants.map(p => <TranslatorTile key={p.identity} participant={p} />)}
      </div>
    </div>
  );
};

// V1.2 — Sidebar verticale right-edge pour la sélection de langue humaine.
// Visible UNIQUEMENT quand au moins un translator est actif dans la room.
// Autohide : 3s sans interaction → opacity 0.3, tap → opacity 1.
const LANG_OPTIONS_SIDEBAR = [
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'sk', flag: '🇸🇰', label: 'Slovensky' },
  { code: 'rw', flag: '🇷🇼', label: 'Kinyarwanda' },
  // 6e liste de langues du fichier — celle du RECEPTEUR. Le japonais avait ete
  // aligne partout ailleurs (5 allowlists serveur + LANG_FLAGS + FLAG_FOR/LABEL_FOR
  // + AVAILABLE_PAIRS) SAUF ici : l emission marchait, l ecoute etait impossible.
  // Toute langue ajoutee doit l etre dans CETTE liste aussi.
  { code: 'ja', flag: '🇯🇵', label: '日本語' },
];
const LangSidebar: React.FC<{
  userLang: string | null;
  setUserLang: (lang: string | null) => void;
}> = ({ userLang, setUserLang }) => {
  const remoteParticipants = useRemoteParticipants();
  const activeTranslators = remoteParticipants.filter(p => {
    const a: any = p.attributes || {};
    return a.role === 'translator' || !!a.targetLang;
  }).length;

  const [active, setActive] = useState(true);
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    const reset = () => {
      setActive(true);
      clearTimeout(t);
      t = setTimeout(() => setActive(false), 3000);
    };
    reset();
    window.addEventListener('touchstart', reset, { passive: true });
    window.addEventListener('pointerdown', reset);
    return () => {
      clearTimeout(t);
      window.removeEventListener('touchstart', reset);
      window.removeEventListener('pointerdown', reset);
    };
  }, []);

  if (activeTranslators === 0) return null;

  return (
    <div
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '6px',
        background: 'rgba(0, 0, 0, 0.55)',
        border: '1px solid rgba(212, 164, 55, 0.15)',
        borderRadius: '999px',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        opacity: active ? 1 : 0.3,
        transition: 'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1)',
        zIndex: 1100,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
      }}
    >
      {LANG_OPTIONS_SIDEBAR.map(l => {
        const selected = userLang === l.code;
        return (
          <button
            key={l.code}
            onClick={() => setUserLang(selected ? null : l.code)}
            title={l.label}
            style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: selected ? 'rgba(212, 164, 55, 0.18)' : 'transparent',
              border: selected
                ? '1px solid rgba(212, 164, 55, 0.7)'
                : '1px solid rgba(212, 164, 55, 0.15)',
              borderRadius: '999px',
              cursor: 'pointer',
              fontSize: '22px',
              lineHeight: 1,
              padding: 0,
              transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
              boxShadow: selected ? '0 0 12px rgba(212, 164, 55, 0.40)' : 'none',
              opacity: selected ? 1 : 0.75,
            }}
          >
            {l.flag}
          </button>
        );
      })}
    </div>
  );
};

// Composant moderne pour l'interface vidéo complète
const ModernVideoInterface: React.FC<{
  userLang: string | null;
  setUserLang: (lang: string | null) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  token: string | null;   // sert a prouver l'identite au serveur (ecriture de humanLang)
}> = ({ userLang, setUserLang, theme, toggleTheme, token }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [isNestorPanelOpen, setIsNestorPanelOpen] = useState(false);
  const [freeFloatMode, setFreeFloatMode] = useState(false);

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      position: 'relative'
    }}>
      <ParticipantGrid freeFloatMode={freeFloatMode} setFreeFloatMode={setFreeFloatMode} />
      <ChatPanel isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
      <ParticipantsPanel isOpen={isParticipantsOpen} onClose={() => setIsParticipantsOpen(false)} />
      <NestorControlPanel isOpen={isNestorPanelOpen} onClose={() => setIsNestorPanelOpen(false)} />
      <CustomControlBar
        onToggleChat={() => setIsChatOpen(!isChatOpen)}
        onToggleParticipants={() => setIsParticipantsOpen(!isParticipantsOpen)}
        onToggleNestor={() => setIsNestorPanelOpen(!isNestorPanelOpen)}
        onToggleFreeFloat={() => setFreeFloatMode(!freeFloatMode)}
        isFreeFloatMode={freeFloatMode}
        isNestorPanelOpen={isNestorPanelOpen}
        userLang={userLang}
        setUserLang={setUserLang}
        theme={theme}
        toggleTheme={toggleTheme}
        token={token}
      />
      {/* V1.2 — Sidebar langue (right edge, autohide, visible si translators actifs) */}
      <LangSidebar userLang={userLang} setUserLang={setUserLang} />
    </div>
  );
};

// Composant pour la page d'accueil
// V1.0 LOBBY — refonte Neo-Cyberpunk Épuré, mobile-first, fullscreen sombre.
// Périmètre : pré-join uniquement. N'altère pas le pipeline LiveKit.
const WelcomePage: React.FC<{
  onJoinRoom: (roomName: string, userName: string, lang: string | null) => Promise<void>;
  initialRoomName: string;
  roomLocked?: boolean;
  experience?: MeetExperience;
}> = ({ onJoinRoom, initialRoomName, roomLocked = false, experience = 'default' }) => {
  // Langue choisie AVANT d'entrer. null = aucune traduction (comportement historique intact :
  // qui ne choisit rien entre comme avant, le choix reste possible dans la salle).
  const [langChoisie, setLangChoisie] = useState<string | null>(null);
  const [userName, setUserName] = useState('');
  const [roomName, setRoomName] = useState(initialRoomName || 'infinity-meet');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusField, setFocusField] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userName.trim()) {
      setError('Entrez votre nom');
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      await onJoinRoom(roomName, userName, langChoisie);
    } catch (err) {
      setError((err as Error).message);
      setIsLoading(false);
    }
  };

  const inputStyle = (field: string): React.CSSProperties => ({
    width: '100%',
    boxSizing: 'border-box',
    padding: '14px 16px',
    height: '48px',
    background: 'rgba(255, 255, 255, 0.04)',
    border: focusField === field
      ? '1px solid rgba(212, 164, 55, 0.7)'
      : '1px solid rgba(212, 164, 55, 0.15)',
    borderRadius: '12px',
    color: '#F0F0F0',
    fontSize: '15px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif',
    fontWeight: 400,
    letterSpacing: '-0.011em',
    outline: 'none',
    boxShadow: focusField === field
      ? '0 0 0 3px rgba(212, 164, 55, 0.18)'
      : 'none',
    transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
    WebkitAppearance: 'none',
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '11px',
    fontWeight: 400,
    color: '#6E6E73',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: '6px',
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      width: '100vw',
      height: '100dvh',
      background: meetBackground(experience),
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: `calc(env(safe-area-inset-top, 0px) + ${isMobile ? '24px' : '48px'})`,
      paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${isMobile ? '24px' : '48px'})`,
      paddingLeft: isMobile ? '20px' : '48px',
      paddingRight: isMobile ? '20px' : '48px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif',
      overflowY: 'auto',
      overflowX: 'hidden',
    }}>
      {/* Halo discret accent jaune — contenu par maxWidth pour éviter overflow horizontal */}
      <div aria-hidden="true" style={{
        position: 'absolute',
        top: '-10%',
        left: 0,
        right: 0,
        margin: '0 auto',
        width: '90vw',
        maxWidth: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(212, 164, 55, 0.10) 0%, transparent 60%)',
        pointerEvents: 'none',
        filter: 'blur(40px)',
      }} />

      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: '420px',
        boxSizing: 'border-box',
        background: 'linear-gradient(180deg, #1a1a2e 0%, #0A0A0F 100%)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(212, 164, 55, 0.15)',
        borderRadius: '20px',
        padding: isMobile ? '28px 24px' : '36px 32px',
        boxShadow: '0 24px 64px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.04)',
        transition: 'all 400ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}>
        {/* Titre identitaire dominant (Commandant V1.1) */}
        <h1 style={{
          margin: '0 0 32px 0',
          fontSize: isMobile ? '24px' : '26px',
          fontWeight: 300,
          color: '#d4a437',
          letterSpacing: '-0.025em',
          textAlign: 'center',
          opacity: 0.95,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
          lineHeight: 1.2,
        }}>
          {experience === 'dojo' ? 'DOJO' : 'Infinity Cloud Communication System'}
        </h1>

        {experience === 'dojo' && (
          <p style={{
            margin: '-20px 0 26px',
            textAlign: 'center',
            color: 'rgba(245, 234, 210, 0.66)',
            fontSize: '12px',
            letterSpacing: '0.08em',
          }}>
            Huit voix, une intelligence commune
          </p>
        )}

        {error && (
          <div role="alert" style={{
            background: 'rgba(239, 68, 68, 0.08)',
            color: '#fca5a5',
            padding: '10px 14px',
            borderRadius: '10px',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            fontSize: '13px',
            marginBottom: '16px',
            letterSpacing: '-0.011em',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
        }}>
          <div>
            <label htmlFor="userName" style={labelStyle}>Nom</label>
            <input
              type="text"
              id="userName"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onFocus={() => setFocusField('userName')}
              onBlur={() => setFocusField(null)}
              placeholder="Votre nom"
              style={inputStyle('userName')}
              autoFocus
              autoCapitalize="words"
              autoComplete="given-name"
            />
          </div>

          {!roomLocked && <div>
            <label htmlFor="roomName" style={labelStyle}>Salle</label>
            <input
              type="text"
              id="roomName"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              onFocus={() => setFocusField('roomName')}
              onBlur={() => setFocusField(null)}
              placeholder="infinity-meet"
              style={inputStyle('roomName')}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>}

          <div>
            <label style={labelStyle}>Votre langue</label>
            {/* GRILLE, pas un flex qui comprime : en flex, « Slovensky » et « Kinyarwanda »
                se faisaient tronquer et le japonais tombait seul sur une ligne pleine largeur.
                Trois colonnes egales = 3 + 2, toutes les langues lisibles, rien qui deborde. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {Object.entries(LANG_FLAGS).map(([code, meta]) => {
                const actif = langChoisie === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLangChoisie(actif ? null : code)}
                    style={{
                      minHeight: '46px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px 10px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      background: actif ? 'rgba(212, 164, 55, 0.16)' : 'rgba(255, 255, 255, 0.04)',
                      border: actif ? '1px solid #d4a437' : '1px solid rgba(255, 255, 255, 0.10)',
                      color: actif ? '#d4a437' : 'rgba(255, 255, 255, 0.75)',
                      fontSize: '13px',
                      fontWeight: 500,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                      letterSpacing: '-0.011em',
                      transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>{meta.flag}</span>
                    <span>{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              marginTop: '6px',
              width: '100%',
              height: '48px',
              background: isLoading ? 'rgba(212, 164, 55, 0.5)' : '#d4a437',
              color: '#0A0A0F',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 500,
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              letterSpacing: '-0.011em',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
              boxShadow: isLoading ? 'none' : '0 4px 16px rgba(212, 164, 55, 0.25)',
            }}
            onMouseEnter={(e) => { if (!isLoading) (e.currentTarget as HTMLButtonElement).style.background = '#FFE873'; }}
            onMouseLeave={(e) => { if (!isLoading) (e.currentTarget as HTMLButtonElement).style.background = '#d4a437'; }}
          >
            {isLoading ? 'Connexion…' : (experience === 'dojo' ? 'Entrer dans le cercle' : 'Connect')}
          </button>
        </form>
      </div>
    </div>
  );
};

const Meet: React.FC<{
  initialRoomName?: string;
  lockRoomName?: boolean;
  experience?: MeetExperience;
}> = ({ initialRoomName = 'infinity-meet', lockRoomName = false, experience = 'default' }) => {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [userName, setUserName] = useState("");
  const [roomName, setRoomName] = useState(initialRoomName);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [isInRoom, setIsInRoom] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);

  // ═══ V0.8 fresh-join — userLang null à chaque connexion (pas de persistance) ═══
  const [userLang, setUserLangState] = useState<string | null>(null);

  const setUserLang = (lang: string | null) => {
    if (lang === null) {
      localStorage.removeItem('meet_user_lang');
    } else {
      localStorage.setItem('meet_user_lang', lang);
    }
    setUserLangState(lang);
  };

  // V0.7 theme toggle : utilisateur peut forcer clair/sombre. Défaut = auto (sombre mobile, clair desktop).
  const [theme, setThemeState] = useState<'light' | 'dark'>(() => {
    const stored = localStorage.getItem('meet_theme');
    if (stored === 'light' || stored === 'dark') return stored;
    return (typeof window !== 'undefined' && window.innerWidth < 768) ? 'dark' : 'light';
  });
  const setTheme = (t: 'light' | 'dark') => {
    localStorage.setItem('meet_theme', t);
    setThemeState(t);
  };
  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');

  // Masquer la navbar du site quand on est en conférence
  useEffect(() => {
    const navbar = document.querySelector('nav');
    if (!showWelcome && navbar) {
      navbar.style.display = 'none';
    }
    return () => {
      if (navbar) {
        navbar.style.display = '';
      }
    };
  }, [showWelcome]);

  // Détecter le paramètre de salle dans l'URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roomParam = urlParams.get('room');

    if (roomParam && !lockRoomName) {
      setRoomName(roomParam);
      console.log(`Salle détectée dans l'URL: ${roomParam}`);

      // Nettoyer l'URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [lockRoomName]);

  const handleConnectionChange = useCallback((state: ConnectionState) => {
    console.log('🔄 CONNECTION STATE CHANGED:', state);
    if (state === ConnectionState.Connected) {
      console.log('✅ CONNECTED - Setting room status');
      setConnectionStatus('connected');
      setIsInRoom(true);
    } else if (state === ConnectionState.Disconnected) {
      // Seulement retourner à la page d'accueil si on était déjà connecté
      if (isInRoom) {
        console.error('❌ DISCONNECTED - Returning to welcome page');
        setConnectionStatus('disconnected');
        setIsInRoom(false);
        setTimeout(() => {
          setShowWelcome(true);
          setToken(null);
        }, 500);
      }
    } else if (state === ConnectionState.Connecting) {
      console.log('⏳ CONNECTING...');
      setConnectionStatus('connecting');
    }
  }, [isInRoom]);

  const handleLeaveRoom = useCallback(() => {
    console.log('Quitter la salle');
    setIsInRoom(false);
    setToken(null);
    setShowWelcome(true);
  }, []);

  const handleJoinRoom = useCallback(async (room: string, name: string, lang: string | null = null) => {
    // La langue choisie a l'entree s'applique AVANT la connexion : l'effet existant publie
    // alors l'attribut humanLang des l'arrivee, sans qu'il ait a toucher aux drapeaux en salle.
    if (lang) setUserLang(lang);
    try {
      setIsConnecting(true);
      setRoomName(room);
      setUserName(name);

      const newToken = await generateToken(room, name);

      if (!newToken) {
        throw new Error("Token non généré par le serveur");
      }

      console.log("Token généré avec succès");
      setToken(newToken);
      // Ne pas mettre isInRoom à true ici - seulement quand vraiment connecté
      setShowWelcome(false);
    } catch (err) {
      console.error("Erreur lors de la génération du token :", err);
      setError((err as Error).message);
      throw err;
    } finally {
      setIsConnecting(false);
    }
  }, []);

  // Auto-disconnect when browser/tab closes
  useEffect(() => {
    if (!isInRoom) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      console.log('🚪 Browser closing - disconnecting from LiveKit...');
      // Note: On ne peut pas faire d'opérations async ici
      // LiveKit se déconnecte automatiquement quand la page se ferme
      // Mais on peut ajouter un message de confirmation
    };

    const handleUnload = () => {
      console.log('🚪 Page unloading - cleanup');
      // Cleanup final
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [isInRoom]);

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: meetBackground(experience),
        padding: '2rem'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px) saturate(150%) brightness(0.95)',
          WebkitBackdropFilter: 'blur(20px) saturate(150%) brightness(0.95)',
          borderRadius: '24px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderTop: '2px solid rgba(255, 255, 255, 0.5)',
          boxShadow: '0 12px 40px rgba(31, 38, 135, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <h1 style={{ color: '#dc2626', marginBottom: '1rem' }}>Impossible de se connecter</h1>
          <p style={{ color: '#374151', marginBottom: '1rem' }}>
            Nous n'avons pas pu établir une connexion avec le serveur.
          </p>
          <p style={{
            background: '#fee2e2',
            color: '#991b1b',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </p>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            Assurez-vous que le serveur de tokens est en cours d'exécution sur le port 4001.
          </p>
          <button
            onClick={() => {
              setError(null);
              setShowWelcome(true);
            }}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '1rem 2rem',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (showWelcome) {
    return (
      <WelcomePage
        onJoinRoom={handleJoinRoom}
        initialRoomName={roomName}
        roomLocked={lockRoomName}
        experience={experience}
      />
    );
  }

  if (isConnecting || !token) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: meetBackground(experience)
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(255, 255, 255, 0.3)',
          borderTopColor: 'white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: 'white', marginTop: '1rem', fontSize: '1.125rem' }}>
          Connexion à LiveKit en cours...
        </p>
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh',
      width: '100vw',
      background: meetBackground(experience),
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Header flottant avec logo et status */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: (typeof window !== 'undefined' && window.innerWidth < 768) ? 'center' : 'space-between',
        alignItems: 'center',
        padding: (typeof window !== 'undefined' && window.innerWidth < 768)
          ? 'calc(env(safe-area-inset-top) + 6px) 12px 6px 12px'
          : '1rem 2rem',
        background: ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768)))
          ? 'rgba(20, 20, 30, 0.6)'
          : 'rgba(255, 255, 255, 0.55)'),
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: ((typeof window !== 'undefined' && (localStorage.getItem('meet_theme') === 'dark' || (localStorage.getItem('meet_theme') === null && window.innerWidth < 768)))
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(0, 0, 0, 0.06)'),
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        zIndex: 1000,
        minHeight: (typeof window !== 'undefined' && window.innerWidth < 768) ? '44px' : 'auto',
      }}>
        <InfinityCloudLogo />
        <StatusBar userName={userName} connectionStatus={connectionStatus} />
      </div>

      {/* V1.1 — Badge room sobre Neo-Cyberpunk */}
      <div style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
        right: '1rem',
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderRadius: '999px',
        padding: '6px 14px',
        border: '1px solid rgba(212, 164, 55, 0.15)',
        zIndex: 1001,
        fontSize: '12px',
        color: '#d4a437',
        fontWeight: 500,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
        letterSpacing: '-0.011em',
        opacity: 0.85,
      }}>
        {experience === 'dojo' ? 'DOJO' : roomName}
      </div>

      <LiveKitRoom
        serverUrl={SERVER_URL}
        token={token}
        connect={true}
        audio={true}
        video={false}
        data-lk-theme="default"
        style={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          background: 'transparent',
          overflowX: 'hidden',
          overscrollBehaviorX: 'none',
        }}
      >
        <ConnectionStateMonitor onConnectionChange={handleConnectionChange} />
        <ModernVideoInterface userLang={userLang} setUserLang={setUserLang} theme={theme} toggleTheme={toggleTheme} token={token} />
        <FilteredAudioRenderer userLang={userLang} />
      </LiveKitRoom>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes audioWave {
          0%, 100% {
            transform: scaleY(0.5);
            opacity: 0.5;
          }
          50% {
            transform: scaleY(1);
            opacity: 1;
          }
        }

        @keyframes pulseRing {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
        @keyframes pulseRingV09 {
          0% {
            transform: scale(1);
            opacity: 0.65;
          }
          70% {
            opacity: 0.15;
          }
          100% {
            transform: scale(1.85);
            opacity: 0;
          }
        }
        @keyframes translatorPulseV09 {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.4);
          }
        }
      `}</style>
    </div>
  );
};

export default Meet;
